import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE, API_ENDPOINTS, APP_VARIANT } from './config';
import { LANGUAGE_KEY } from '@/src/lib/i18n';
import { getItem, setItem, removeItem } from '../lib/storage';
import { clearOwnerDriverStorage } from '../features/owner/ownerDriverPrompt';
import sentry from '../utils/sentry';
import { CLIENT_PLATFORM, CLIENT_RELEASE } from '../lib/buildInfo';
import type { OnboardingState, OnboardingPersona } from '../utils/onboardingResume';

export type ApiErrorType = 'NETWORK' | 'UNKNOWN';

const reportedNetworkFailures = new Set<string>();

export const __resetNetworkFailureReports = () => {
  reportedNetworkFailures.clear();
};

const isLikelyOfflineError = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err || '');
  return /network request failed|networkerror|failed to fetch|no internet|offline/i.test(message);
};

export interface ApiResponse<T> {
  data: T;
  ok: boolean;
  status: number;
  errorType?: ApiErrorType;
  errorMessage?: string;
}

// Password reset error response interface
export interface PasswordResetErrorResponse {
  detail?: string;
  new_password1?: string[];
  new_password2?: string[];
  token?: string[];
  uid?: string[];
  non_field_errors?: string[];
  [key: string]: any;
}

export interface ErrorResponse {
  odometer?: string[] | string;
  odometer_decrease_detected?: boolean;
  previous_session_out?: {
    id: number;
    odometer: number;
    timestamp: string;
  };
  [key: string]: any;
}

// General API Error response type
export type ApiResponseError = PasswordResetErrorResponse | ErrorResponse | { detail?: string; message?: string; non_field_errors?: string[] };

export interface TimeLogEntry {
  id: number;
  user: number;
  company: number;
  vehicle: number | null;
  timestamp: string;
  location: string | null;
  odometer: number | null;
  event_type: 'session_in' | 'session_out' | 'drive_in' | 'drive_out';
  company_name: string;
  vehicle_registration_number: string | null;
  edited?: boolean;
  observations?: string | null;
}

// Odometer velocity warning type
export interface OdometerVelocityWarning {
  exceeds_limit: boolean;
  mean_velocity_kmh: number;
  previous_odometer: number;
  previous_timestamp: string;
  odometer_difference_km: number;
  time_difference_hours: number;
}

// New response type for session/driving actions that return complete state
export interface SessionActionResponse extends TimeLogEntry {
  current_state: {
    is_session_active: boolean;
    is_driving: boolean;
    // When the current state began (ALL-136); absent on older backends.
    state_since?: string | null;
  };
  driving_time_today: string;
  // Absent on backends older than the rolling 24h limit (Lei 45/2018).
  driving_limit?: DrivingLimitStatus;
  driving_week_limits?: DrivingWeekLimits | null;
  odometer_velocity_warning?: OdometerVelocityWarning;
}

// Rolling 24h driving total (Lei 45/2018, art. 13). Informational only.
export interface DrivingLimitStatus {
  status: 'ok' | 'warning' | 'reached';
  is_driving: boolean;
  window_hours: number;
  window_start: string;
  calculated_at: string;
  limit_seconds: number;
  warning_lead_seconds: number;
  driven_seconds: number;
  remaining_seconds: number;
  seconds_until_warning: number | null;
  seconds_until_limit: number | null;
  below_limit_again_at: string | null;
}

// Weekly driving total and rolling 17-week weekly average (ALL-136). Reference
// limits only. Each part is null when the driver has no records in its window.
interface DrivingThresholds {
  is_driving: boolean;
  // Driving time until 80 %, 95 % and 100 %; null when not driving, already
  // crossed, or after the end of the week.
  seconds_until_warning: number | null;
  seconds_until_critical: number | null;
  seconds_until_limit: number | null;
  calculated_at: string;
}

export interface WeekDrivingLimitStatus extends DrivingThresholds {
  driven_seconds: number;
  limit_seconds: number;
  remaining_seconds: number;
  week_start: string;
  week_end: string;
}

export interface AverageDrivingLimitStatus extends DrivingThresholds {
  average_weekly_seconds: number;
  limit_weekly_seconds: number;
  window_weeks: number;
  allowed_this_week_seconds: number;
  window_start: string;
  window_end: string;
  has_full_history: boolean;
}

export interface DrivingWeekLimits {
  week: WeekDrivingLimitStatus | null;
  average: AverageDrivingLimitStatus | null;
}

export interface DriverStatusResponse {
  is_session_active: boolean;
  is_driving: boolean;
  state_since?: string | null;
  driving_time_today: string;
  driving_limit?: DrivingLimitStatus;
  driving_week_limits?: DrivingWeekLimits | null;
}

interface Employment {
  id: number;
  user_email: string;
  user_full_name: string;
  company_nipc: string;
  role: string;
  is_active: boolean;
  activated_at?: string | null;
  deactivated_at?: string | null;
  has_ever_been_activated?: boolean;
}

interface UserProfile {
  nif: string;
  address: string | null;
  license_number: string | null;
  date_of_birth: string | null;
  notification_enabled: boolean | null;
  stop_time_threshold: number | null;
  distance_threshold: number | null;
}

interface Company {
  id: number;
  name: string;
  nipc: string;
  is_individual?: boolean;
  certidao_permanente: string;
  numero_licenca: string;
  address: string | null;
  email: string | null;
  phone_number: string | null;
  active_drivers?: number;
  total_licenses?: number;
  available_licenses?: number;
}

export interface CompanyLicenseSummary {
  total: number;
  hours: number;
  gps: number;
  available_hours: number;
  available_gps: number;
}

export interface FundingDisplay {
  payer: 'company' | 'self' | 'ambiguous' | 'uncovered';
  coverage_status: 'active' | 'awaiting' | 'not_covered' | 'unknown';
  coverage_source: 'personal_subscription' | 'company_subscription' | 'none';
  seat_consumption: number;
  consumes_corporate_seat: boolean;
  /** ALL-126: why a self-pay licence stopped covering work; null when it did not fail payment. */
  license_issue?: 'past_due' | 'unpaid' | 'canceled_nonpayment' | null;
}

export interface OwnedCompanyCapacity {
  purchased: number;
  used: number;
  reserved: number;
  available: number;
  drivers: Record<string, number>;
  active_drivers?: {
    total: number;
    company: number;
    self: number;
    ambiguous: number;
  };
}

export interface OwnedCompanySummary {
  id: number;
  name: string;
  nipc: string;
  license_summary: CompanyLicenseSummary;
  capacity?: OwnedCompanyCapacity;
  active_drivers: number;
  has_active_subscription: boolean;
}

export interface ProfileRecoveryContract {
  eligible: boolean;
  missing_fields: Array<'nif' | 'address' | 'license_number' | 'date_of_birth'>;
  reason:
    | 'valid_personal_coverage'
    | 'valid_company_coverage'
    | 'no_valid_personal_coverage'
    | 'profile_complete';
}

export interface UserDetails {
  id?: number;
  email: string;
  full_name: string;
  phone_number: string | null;
  phone_verified?: boolean;
  /** CRM-only fleet size range declared in owner onboarding (#208) — never feeds pricing. */
  fleet_size_range?: string | null;
  /** CRM-only owner self-driver intent captured during registration (#272). */
  also_driver_intent?: 'yes' | 'no' | null;
  /**
   * D-046 derived owner-driver activation state for the user's owned company
   * (null when the user owns no company). Server-authoritative; drives the
   * persistent missing-licence warning and the explicit activation/retry path.
   */
  driver_activation?: DriverActivationState | null;
  profile: UserProfile | null;
  profile_recovery?: ProfileRecoveryContract;
  company: Company | null;
  employments: Employment[];
  groups: string[];
  owned_companies?: OwnedCompanySummary[];
}

interface Vehicle {
  id: number;
  registration_number: string;
  fuel_type: string;
  make: string;
  model: string;
  year: number;
  company: number | null;
  drivers: number[];
  pending_activation?: boolean;
}

export interface WeeklyDrivingReport {
  week_start: string;
  week_end: string;
  daily_hours: {
    [date: string]: {
      vehicle_reg: string | null;
      odometer_start: number | null;
      odometer_end: number | null;
      total_driving_time: { hours: number; minutes: number };
      total_availability_time: { hours: number; minutes: number };
      total_rest_time: { hours: number; minutes: number };
      events: {
        event_type: 'driving' | 'availability';
        start: string; // Formato HH:MM
        end: string; // Formato HH:MM
        start_location: string | null;
        end_location: string | null;
        total: number; // Total em minutos
        company_id?: number | null;
        company_name?: string | null;
      }[];
      observations: string[];
      attribution?: {
        company_id: number | null;
        company_name: string | null;
        payer: 'company' | 'self' | 'ambiguous' | 'uncovered' | 'unknown';
        coverage_status: string;
        attribution_state: string;
        companies?: {
          company_id: number | null;
          company_name: string | null;
          payer: 'company' | 'self' | 'ambiguous' | 'uncovered' | 'unknown';
          coverage_status: string;
          attribution_state: string;
        }[];
      };
      attributions?: {
        company_id: number | null;
        company_name: string | null;
        tax_identifier_kind?: 'nif' | 'nipc' | null;
        tax_identifier?: string | null;
        address?: string | null;
        email?: string | null;
        payer: 'company' | 'self' | 'ambiguous' | 'uncovered' | 'unknown';
        coverage_status: string;
        attribution_state: string;
      }[];
      attribution_company_ids?: number[];
      personal_work?: boolean;
    };
  };
  total_week: {
    driving: { hours: number; minutes: number };
    availability: { hours: number; minutes: number };
    rest: { hours: number; minutes: number };
  };
  driver_details: {
    name: string;
    email: string;
    phone_number: string | null;
    date_of_birth: string | null;
    nif: string | null;
    address: string | null;
    license_number: string | null;
  };
  company_details: {
    name: string | null;
    nipc: string | null;
    tax_identifier_kind?: 'nif' | 'nipc' | null;
    tax_identifier?: string | null;
    address: string | null;
    email: string | null;
  };
  company_attributions?: NonNullable<WeeklyDrivingReport['daily_hours'][string]['attributions']>;
}

export interface CompanyLicenses {
  subscriptions: {
    id: number;
    status: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    licenses: number;
    created_at: string;
  }[];
  summary: {
    total_licenses: number;
    available_licenses: number;
    hours_licenses?: number;
    gps_licenses?: number;
    available_gps_licenses?: number;
    active_drivers: number;
    active_subscriptions_count: number;
    ever_had_licenses: boolean;
    capacity?: OwnedCompanyCapacity;
    /** Company-level GPS usage contract accepted at (one for all devices, at purchase). */
    gps_contract_accepted_at?: string | null;
    /** D-059: true while this company still has to accept the published GPS contract. */
    gps_contract_required?: boolean;
  };
}

// Headers autenticados
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getItem('userToken');
  const language = await getLanguageHeader();
  
  return {
    Authorization: token ? `Token ${token}` : 'Token ',
    'Content-Type': 'application/json',
    ...language,
  };
};

// Get language from AsyncStorage
const getLanguageHeader = async (): Promise<Record<string, string>> => {
  const language = await getItem(LANGUAGE_KEY);
  return language ? { 'Accept-Language': language } : {};
};

// Headers públicos (sem autenticação)
const getPublicHeaders = async (): Promise<Record<string, string>> => {
  const language = await getLanguageHeader();
  return {
    'Content-Type': 'application/json',
    ...language,
  };
};

export const SELECTED_COMPANY_ID_STORAGE_KEY = 'selectedCompanyId';
let activeCompanyIdCache: string | null = null;

export const getActiveCompanyContextId = async (): Promise<string | null> => {
  if (activeCompanyIdCache !== null) {
    return activeCompanyIdCache;
  }
  const storedId = await getItem(SELECTED_COMPANY_ID_STORAGE_KEY);
  activeCompanyIdCache = storedId;
  return storedId;
};

export const setActiveCompanyContextId = async (companyId: string | number | null): Promise<void> => {
  const normalized = companyId === null || companyId === undefined ? null : String(companyId);
  activeCompanyIdCache = normalized;
  if (normalized === null) {
    await removeItem(SELECTED_COMPANY_ID_STORAGE_KEY);
  } else {
    await setItem(SELECTED_COMPANY_ID_STORAGE_KEY, normalized);
  }
  clearApiCache();
};

const buildCompanyHeader = async (companyId?: string | number | null): Promise<Record<string, string>> => {
  const normalized = companyId === null || companyId === undefined ? null : String(companyId);
  const resolvedCompanyId = normalized ?? (await getActiveCompanyContextId());
  if (!resolvedCompanyId) {
    return {};
  }
  return { 'X-Company-ID': resolvedCompanyId };
};

const withCompanyContext = (
  options: ApiRequestInit = {},
  companyId?: string | number | null
): ApiRequestInit => {
  if (companyId === undefined || companyId === null) {
    return options;
  }
  return {
    ...options,
    companyId,
  };
};

const normalizeHeadersInit = (input?: HeadersInit): Record<string, string> => {
  if (!input) {
    return {};
  }
  if (input instanceof Headers) {
    const normalized: Record<string, string> = {};
    input.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }
  if (Array.isArray(input)) {
    return input.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }
  return { ...(input as Record<string, string>) };
};

// Add request cache
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 seconds cache duration

type Primitive = string | number | boolean | null | undefined;

export type ApiRequestInit = RequestInit & {
  params?: Record<string, Primitive | Primitive[]>;
  companyId?: string | number;
  includeCompanyContext?: boolean;
  /** Overrides DEFAULT_REQUEST_TIMEOUT_MS for this call. */
  timeoutMs?: number;
  /**
   * When true, the request is attempted exactly ONCE on the wire: no transient
   * network retry and no production canonical-host fallback. Required for
   * single-use tokens (email-possession links) whose replay after a consumed
   * attempt would be unsafe. Defaults to false (existing behaviour preserved).
   */
  singleAttempt?: boolean;
};

// fetch has no deadline of its own, and RN's never rejects when a socket is
// stranded mid-flight — a network handover, or Android suspending the JS thread
// on screen-lock. A driver's start-driving POST hung that way and left the
// dashboard spinning on "Iniciando..." with no path back (#316).
// 30s is deliberately generous: /api/timelogs/ is unpaginated, so a driver with
// a long history legitimately needs seconds to download it on mobile data.
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

type StripeOperationState = {
  token: string;
  inFlight: number;
  preserveWhenIdle: boolean;
  clearWhenIdle: boolean;
};

const stripeOperationTokens = new Map<string, StripeOperationState>();

const createOpaqueOperationToken = (): string => {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
    throw new Error('Secure random generation is unavailable');
  }
  const bytes = new Uint8Array(24);
  cryptoApi.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
};

const stableOperationValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stableOperationValue);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        result[key] = stableOperationValue((value as Record<string, unknown>)[key]);
        return result;
      }, {});
  }
  return value;
};

const stripeOperationFingerprint = (
  operation: string,
  payload: unknown,
  companyId?: number | string
): string => JSON.stringify([
  operation,
  companyId === undefined ? null : String(companyId),
  stableOperationValue(payload),
]);

const apiFetchWithOperationToken = async <T>(
  fingerprint: string,
  endpoint: string,
  options: ApiRequestInit
): Promise<ApiResponse<T>> => {
  let operationState = stripeOperationTokens.get(fingerprint);
  if (!operationState) {
    operationState = {
      token: createOpaqueOperationToken(),
      inFlight: 0,
      preserveWhenIdle: false,
      clearWhenIdle: false,
    };
    stripeOperationTokens.set(fingerprint, operationState);
  }

  const operationToken = operationState.token;
  operationState.inFlight += 1;

  try {
    const response = await apiFetch<T>(endpoint, {
      ...options,
      headers: {
        ...normalizeHeadersInit(options.headers),
        'X-Operation-Token': operationToken,
      },
    });

    // A transport failure or any 5xx makes the outcome ambiguous. Across
    // concurrent calls sharing this fingerprint, ambiguity wins over a
    // definitive response so that a later retry keeps the original token.
    if (response.status === 0 || response.status >= 500) {
      operationState.preserveWhenIdle = true;
    } else {
      operationState.clearWhenIdle = true;
    }
    return response;
  } catch (error) {
    operationState.preserveWhenIdle = true;
    throw error;
  } finally {
    operationState.inFlight -= 1;
    if (
      operationState.inFlight === 0
      && stripeOperationTokens.get(fingerprint) === operationState
    ) {
      if (operationState.preserveWhenIdle) {
        // Preserve for the next retry, but start a fresh completion epoch so
        // that a subsequent unambiguous response can rotate the token.
        operationState.preserveWhenIdle = false;
        operationState.clearWhenIdle = false;
      } else if (operationState.clearWhenIdle) {
        stripeOperationTokens.delete(fingerprint);
      }
    }
  }
};

const cancelStripeOperation = (fingerprint: string): void => {
  stripeOperationTokens.delete(fingerprint);
};

export const __resetOperationTokens = (): void => {
  stripeOperationTokens.clear();
};

// Generic fetch with caching
export const apiFetch = async <T = any>(
  endpoint: string,
  options: ApiRequestInit = {},
  requiresAuth: boolean = true,
  useCache: boolean = true
): Promise<ApiResponse<T>> => {
  try {
    const {
      params,
      companyId,
      includeCompanyContext = true,
      headers: requestHeaders,
      timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
      singleAttempt = false,
      ...fetchOptions
    } = options;

    let requestUrl = endpoint;

    if (params && Object.keys(params).length > 0) {
      const url = endpoint.startsWith('http')
        ? new URL(endpoint)
        : new URL(endpoint, 'https://dummy-base.local');

      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              url.searchParams.append(key, String(item));
            }
          });
        } else if (value !== null) {
          url.searchParams.append(key, String(value));
        }
      });

      requestUrl = endpoint.startsWith('http')
        ? url.toString()
        : `${url.pathname}${url.search}`;
    }

    const method = (fetchOptions.method || 'GET').toUpperCase();

    let headers = {
      ...(requiresAuth ? await getAuthHeaders() : await getPublicHeaders()),
      ...normalizeHeadersInit(requestHeaders),
    };

    if (requiresAuth && includeCompanyContext) {
      headers = {
        ...headers,
        ...(await buildCompanyHeader(companyId)),
      };
    }

    const cacheKey = `${headers.Authorization || ''}|${headers['X-Company-ID'] || ''}|${headers['Accept-Language'] || ''}|${requestUrl}`;

    if (useCache && method === 'GET') {
      const cached = requestCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return {
          ok: true,
          data: cached.data,
          status: 200,
        };
      }
    }

    const parseResponse = async (response: Response) => {
      let parsed: any = null;
      const contentType =
        typeof (response as any).headers?.get === 'function'
          ? (response as any).headers.get('content-type') || ''
          : 'application/json';

      try {
        if (contentType.includes('application/json') && typeof (response as any).json === 'function') {
          parsed = await response.json();
        } else if (typeof (response as any).text === 'function') {
          const text = await response.text();
          parsed = text ? { detail: text } : null;
        }
      } catch (parseError) {
        console.warn('apiFetch response parse warning', parseError);
        parsed = null;
      }
      return parsed;
    };

    // One deadline for the whole call rather than one per attempt: the retry
    // and the canonical-host fallback below would otherwise stack up to three
    // full budgets, reintroducing the unbounded wait this is meant to cap.
    const deadline = new AbortController();
    let timedOut = false;
    const deadlineHandle = setTimeout(() => {
      timedOut = true;
      deadline.abort();
    }, timeoutMs);

    const timeoutFailure = (): ApiResponse<T> => {
      const failureKey = `${method}:${requestUrl}`;
      if (!reportedNetworkFailures.has(failureKey)) {
        reportedNetworkFailures.add(failureKey);
        sentry.addBreadcrumb({
          category: 'network',
          level: 'warning',
          message: `apiFetch timeout after ${timeoutMs}ms method=${method} url=${requestUrl}`,
        });
      }
      console.error('apiFetch timeout', method, requestUrl);
      return {
        ok: false,
        data: null as any,
        status: 0,
        // NETWORK, so withNetworkRetry treats a timeout as retryable and the
        // callers that already branch on transport failure (stop-driving's
        // offline queue) keep working unchanged.
        errorType: 'NETWORK',
        errorMessage: `Request timed out after ${timeoutMs}ms`,
      };
    };

    const networkFailure = (failure: unknown): ApiResponse<T> => {
      const failureMessage = failure instanceof Error ? failure.message : String(failure || 'Network request failed');
      const failureKey = `${method}:${requestUrl}`;
      // Skip Sentry reports when the device is offline (canonical RN error).
      // Throttle other network errors to one breadcrumb per (method, url) per
      // session. Breadcrumb (not captureMessage): plain network failures are
      // user-environment noise as standalone events (ALLDRIVERS-REACT-NATIVE-3R /
      // ALLDRIVERS-WEB-E) but still show up as context on the next real error.
      if (!isLikelyOfflineError(failure) && !reportedNetworkFailures.has(failureKey)) {
        reportedNetworkFailures.add(failureKey);
        sentry.addBreadcrumb({
          category: 'network',
          level: 'warning',
          message: `apiFetch network failure method=${method} url=${requestUrl}`,
        });
      }
      console.error('apiFetch error', failure);
      return {
        ok: false,
        data: null as any,
        status: 0,
        errorType: 'NETWORK',
        errorMessage: failureMessage,
      };
    };

    const executeFetch = async (url: string): Promise<ApiResponse<T>> => {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: deadline.signal,
      });

      const data = await parseResponse(response);

      if (useCache && response.ok && method === 'GET') {
        requestCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
      }

      return {
        ok: response.ok,
        data: data as T,
        status: response.status,
      };
    };

    try {
      try {
        return await executeFetch(requestUrl);
      } catch (firstError) {
        // The budget is spent; retrying or switching host only stalls the
        // caller further on a signal that is already aborted.
        if (timedOut) {
          return timeoutFailure();
        }
        // Single-use tokens must be attempted exactly once: a transient retry or
        // the canonical-host fallback below could replay a token the server may
        // already have consumed. Callers opt in per request.
        if (singleAttempt) {
          return networkFailure(firstError);
        }
        // Retry once on transient network failures
        try {
          await new Promise((resolve) => setTimeout(resolve, 250));
          return await executeFetch(requestUrl);
        } catch (retryError) {
          if (timedOut) {
            return timeoutFailure();
          }
          // Production fallback: only allow canonical-host retry in production.
          // Never switch local/dev traffic to app.alldrivers.pt.
          try {
            const primaryBase = String(API_BASE || '').replace(/\/$/, '');
            const canonicalBase = 'https://app.alldrivers.pt';
            const maybeAbsolute = requestUrl.startsWith('http://') || requestUrl.startsWith('https://');
            const isLocalBase = /(^https?:\/\/)?(localhost|127\.0\.0\.1|192\.168\.|10\.|172\.(1[6-9]|2\d|3[0-1]))/i.test(primaryBase);
            if (APP_VARIANT === 'production' && maybeAbsolute && primaryBase && primaryBase !== canonicalBase && !isLocalBase && requestUrl.startsWith(primaryBase)) {
              const fallbackUrl = requestUrl.replace(primaryBase, canonicalBase);
              const fallbackResponse = await executeFetch(fallbackUrl);

              sentry.captureMessage(
                `apiFetch fallback host applied: ${primaryBase} -> ${canonicalBase}`,
                'warning'
              );

              return fallbackResponse;
            }
          } catch (fallbackError) {
            console.warn('apiFetch fallback host failed', fallbackError);
          }

          // The canonical-host attempt can burn what is left of the budget.
          if (timedOut) {
            return timeoutFailure();
          }

          const failure = retryError || firstError;
          return networkFailure(failure);
        }
      }
    } finally {
      clearTimeout(deadlineHandle);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error || 'Unexpected API failure');
    console.error('apiFetch unexpected error', error);
    return {
      ok: false,
      data: null as any,
      status: 0,
      errorType: 'UNKNOWN',
      errorMessage: message,
    };
  }
};

// Clear cache for specific endpoint
export const clearApiCache = (endpoint?: string) => {
  if (endpoint) {
    const suffix = `|${endpoint}`;
    for (const key of requestCache.keys()) {
      if (key === endpoint || key.endsWith(suffix)) {
        requestCache.delete(key);
      }
    }
  } else {
    requestCache.clear();
  }
};

// Fetch para blobs (com autenticação)
export const apiFetchBlob = async (url: string, options: ApiRequestInit = {}): Promise<ApiResponse<Blob>> => {
  const {
    companyId,
    includeCompanyContext = true,
    headers: requestHeaders,
    ...rest
  } = options;

  let headers = {
    ...(await getAuthHeaders()),
    ...normalizeHeadersInit(requestHeaders),
  };
  delete headers['Content-Type'];

  if (includeCompanyContext) {
    headers = {
      ...headers,
      ...(await buildCompanyHeader(companyId)),
    };
  }

  const response = await fetch(url, { ...rest, headers });
  const data = await response.blob();
  return { data, ok: response.ok, status: response.status };
};

// Endpoints
export const endSession = async (
  vehicle: number,
  odometer: number,
  location: string,
  observations?: string,
  confirmVelocity?: boolean
): Promise<ApiResponse<SessionActionResponse | ErrorResponse>> => {
  return apiFetch<SessionActionResponse | ErrorResponse>(`${API_ENDPOINTS.TIMELOGS}end-session/`, {
    method: 'POST',
    body: JSON.stringify({ vehicle, odometer, location, observations, confirm_velocity: confirmVelocity }),
  });
};

export const login = async (email: string, password: string): Promise<ApiResponse<{ key: string }>> => {

  return apiFetch<{ key: string }>(API_ENDPOINTS.LOGIN, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }, false);
};

export interface MagicLinkVerifyResponse { key: string }

export const requestMagicLink = async (email: string): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(API_ENDPOINTS.MAGIC_LINK_REQUEST, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }, false);
};

export const verifyMagicLink = async (token: string): Promise<ApiResponse<MagicLinkVerifyResponse>> => {
  return apiFetch<MagicLinkVerifyResponse>(API_ENDPOINTS.MAGIC_LINK_VERIFY, {
    method: 'POST',
    body: JSON.stringify({ token }),
  }, false);
};

export const requestPasswordReset = async (email: string): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(API_ENDPOINTS.PASSWORD_RESET, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }, false);
};

export const verifyPasswordResetToken = async (uid: string, token: string): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(API_ENDPOINTS.PASSWORD_RESET_VERIFY, {
    method: 'POST',
    body: JSON.stringify({ uid, token }),
  }, false);
};

export const confirmPasswordReset = async (
  uid: string,
  token: string,
  newPassword1: string,
  newPassword2: string
): Promise<ApiResponse<any>> => {
  try {
    // Remove trailing slash from endpoint and construct URL properly
    const endpoint = API_ENDPOINTS.PASSWORD_RESET_CONFIRM.replace(/\/$/, '');
    const response = await apiFetch<any>(`${endpoint}/${uid}/${token}/`, {
      method: 'POST',
      body: JSON.stringify({
        new_password1: newPassword1,
        new_password2: newPassword2,
      }),
    }, false);  // Set requiresAuth to false since this is a public endpoint
    return response;
  } catch (error: any) {
    return {
      ok: false,
      data: error.response?.data || { message: 'Failed to confirm password reset' },
      status: error.response?.status || 500,
    };
  }
};

export interface PasswordChangeErrorResponse {
  detail?: string;
  old_password?: string[];
  new_password1?: string[];
  new_password2?: string[];
  non_field_errors?: string[];
  [key: string]: any;
}

export const changePassword = async (
  oldPassword: string,
  newPassword1: string,
  newPassword2: string
): Promise<ApiResponse<{ detail?: string }>> => {
  return apiFetch<{ detail?: string }>(API_ENDPOINTS.PASSWORD_CHANGE, {
    method: 'POST',
    body: JSON.stringify({
      old_password: oldPassword,
      new_password1: newPassword1,
      new_password2: newPassword2,
    }),
  });
};

export const getUserDetails = async (
  options: { fresh?: boolean } = {},
): Promise<ApiResponse<UserDetails>> => {
  return apiFetch<UserDetails>(
    API_ENDPOINTS.USER_DETAILS,
    { method: 'GET' },
    true,
    !options.fresh,
  );
};

export interface ProfileRecoveryUpdate {
  nif?: string;
  address?: string;
  license_number?: string;
  date_of_birth?: string;
}

/** Update only the already-authenticated driver's missing profile fields. */
export const updateUserProfile = async (
  profile: ProfileRecoveryUpdate,
): Promise<ApiResponse<UserDetails>> => {
  const response = await apiFetch<UserDetails>(API_ENDPOINTS.USER_DETAILS, {
    method: 'PATCH',
    headers: { 'X-Profile-Recovery': 'true' },
    body: JSON.stringify({ profile }),
  });
  // A successful recovery changes both server reads the next route decides on.
  // Without evicting them, the dashboard guard re-reads the cached pre-write
  // onboarding state (`current_step='nif'`) within the cache window and sends
  // the driver back to /register/driver, which re-reads the stale users/me.
  if (response.ok) {
    clearApiCache(API_ENDPOINTS.USER_DETAILS);
    clearApiCache(API_ENDPOINTS.ONBOARDING_STATE);
  }
  return response;
};

/**
 * Read the server-authoritative onboarding state. Pass `{ fresh: true }` to
 * bypass the short-lived GET cache — required after any write so a stale
 * pre-write snapshot cannot false-green a resume.
 */
export const getOnboardingState = async (
  options: { fresh?: boolean } = {},
): Promise<ApiResponse<OnboardingState>> => {
  if (options.fresh) clearApiCache(API_ENDPOINTS.ONBOARDING_STATE);
  return apiFetch<OnboardingState>(
    API_ENDPOINTS.ONBOARDING_STATE,
    { method: 'GET' },
    true,
    !options.fresh,
  );
};

export const advanceOnboardingState = async (
  persona: OnboardingPersona,
  currentStep: string,
  evidence?: Record<string, unknown>,
): Promise<ApiResponse<OnboardingState>> => {
  const response = await withNetworkRetry(
    () => apiFetch<OnboardingState>(API_ENDPOINTS.ONBOARDING_STATE, {
      method: 'PATCH',
      body: JSON.stringify({ persona, current_step: currentStep, evidence }),
    }),
    2,
    250,
  );
  // A successful write invalidates the cached GET snapshot so a follow-up read
  // (even without { fresh: true }) returns authoritative state, never a stale
  // pre-write false green.
  if (response.ok) clearApiCache(API_ENDPOINTS.ONBOARDING_STATE);
  return response;
};

// Two-step email change (#188): requesting a change never mutates the
// account's email immediately — a confirmation link is sent to the new
// address and the account email only updates once that link is clicked.
export interface EmailChangeStatus {
  pending: boolean;
  new_email: string | null;
  rate_limited: boolean;
}

export const requestEmailChange = async (
  newEmail: string
): Promise<ApiResponse<{ detail?: string }>> => {
  return apiFetch<{ detail?: string }>(API_ENDPOINTS.EMAIL_CHANGE_REQUEST, {
    method: 'POST',
    body: JSON.stringify({ new_email: newEmail }),
  });
};

export const getEmailChangeStatus = async (): Promise<ApiResponse<EmailChangeStatus>> => {
  return apiFetch<EmailChangeStatus>(API_ENDPOINTS.EMAIL_CHANGE_STATUS, { method: 'GET' });
};

// Public: check if email is available
export const checkEmailAvailable = async (
  email: string
): Promise<ApiResponse<{ available: boolean; reason?: string }>> => {
  return apiFetch<{ available: boolean; reason?: string }>(
    `${API_ENDPOINTS.CHECK_EMAIL}?email=${encodeURIComponent(email)}`,
    { method: 'GET' },
    false
  );
};

// Public: check if NIPC is available under owner rules
export const checkNipcAvailable = async (
  nipc: string
): Promise<ApiResponse<{ available: boolean; valid?: boolean; kind?: 'nif' | 'nipc'; reason_code?: string; reason?: string; action?: string; actions?: string[] }>> => {
  return apiFetch<{ available: boolean; valid?: boolean; kind?: 'nif' | 'nipc'; reason_code?: string; reason?: string; action?: string; actions?: string[] }>(
    `${API_ENDPOINTS.CHECK_NIPC}?nipc=${encodeURIComponent(nipc)}`,
    { method: 'GET' },
    false
  );
};

export interface OwnerSupportRequestPayload {
  tax_identifier: string;
  email: string;
  phone: string;
  description: string;
}

/** Explicit support CTA side effect; the server returns no tenant details. */
export const requestOwnerSupport = async (
  payload: OwnerSupportRequestPayload,
): Promise<ApiResponse<{ detail?: string }>> => {
  return apiFetch<{ detail?: string }>(
    API_ENDPOINTS.OWNER_SUPPORT_REQUEST,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    false,
  );
};

/** Capability-gated account-only registration from an owner-support invite. */
export const registerSupportAccount = async (
  payload: {
    support_token: string;
    email: string;
    password: string;
    full_name: string;
    phone_number: string;
  },
): Promise<ApiResponse<{ detail?: string; token?: string }>> => {
  return apiFetch<{ detail?: string; token?: string }>(
    `${API_BASE}/api/users/register-owner/`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    false,
  );
};

// Public: check if NIF is available and valid
export const checkNifAvailable = async (
  nif: string
): Promise<ApiResponse<{ available: boolean; reason?: string }>> => {
  return apiFetch<{ available: boolean; reason?: string }>(
    `${API_ENDPOINTS.CHECK_NIF}?nif=${encodeURIComponent(nif)}`,
    { method: 'GET' },
    false
  );
};

// Public: check if CMTVDE (license_number) is available
export const checkLicenseNumberAvailable = async (
  licenseNumber: string
): Promise<ApiResponse<{ available: boolean; reason?: string }>> => {
  return apiFetch<{ available: boolean; reason?: string }>(
    `${API_ENDPOINTS.CHECK_LICENSE}?license_number=${encodeURIComponent(licenseNumber)}`,
    { method: 'GET' },
    false
  );
};

export const addCompany = async (
  company_name: string,
  company_nipc: string,
  company_email: string
): Promise<ApiResponse<any>> => {
  return apiFetch<any>(API_ENDPOINTS.ADD_COMPANY, {
    method: 'POST',
    body: JSON.stringify({ company_name, company_nipc, company_email }),
  });
};

/**
 * Add a company as an owner (uses owner's email automatically)
 * Endpoint: POST /api/companies/register-owner/
 */
export const addCompanyAsOwner = async (
  name: string,
  nipc: string,
  certidao_permanente?: string,
  tipo_licenca_operacao?: string,
  numero_licenca?: string
): Promise<ApiResponse<any>> => {
  const body: any = {
    name,
    nipc,
  };
  
  if (certidao_permanente) {
    body.certidao_permanente = certidao_permanente;
  }
  if (tipo_licenca_operacao) {
    body.tipo_licenca_operacao = tipo_licenca_operacao;
  }
  if (numero_licenca) {
    body.numero_licenca = numero_licenca;
  }
  
  return apiFetch<any>(`${API_ENDPOINTS.COMPANIES}register-owner/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const getDriverVehicles = async (): Promise<ApiResponse<{ has_vehicles: boolean; vehicles: Vehicle[] }>> => {
  return apiFetch<{ has_vehicles: boolean; vehicles: Vehicle[] }>(API_ENDPOINTS.DRIVER_VEHICLES, {
    method: 'GET',
  });
};

export const addVehicle = async (
  registration_number: string,
  fuel_type: string,
  make: string,
  model: string,
  year: number
): Promise<ApiResponse<Vehicle>> => {
  return apiFetch<Vehicle>(API_ENDPOINTS.VEHICLES, {
    method: 'POST',
    body: JSON.stringify({ registration_number, fuel_type, make, model, year }),
  });
};

// Company-scoped vehicles (owner/manager) with pagination
export const getCompanyVehicles = async (
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  ordering?: string,
  companyId?: number
): Promise<ApiResponse<{
  results: Vehicle[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: boolean;
  previous: boolean;
}>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (search) params.append('search', search);
  if (ordering) params.append('ordering', ordering);
  const options: ApiRequestInit = { method: 'GET' };
  if (companyId !== undefined) {
    options.companyId = companyId;
  }
  return apiFetch<{
    results: Vehicle[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    next: boolean;
    previous: boolean;
  }>(`${API_ENDPOINTS.VEHICLES}company/?${params.toString()}`, options);
};

export const createCompanyVehicle = async (
  registration_number: string,
  fuel_type?: string,
  make?: string,
  model?: string,
  year?: number
): Promise<ApiResponse<Vehicle>> => {
  return apiFetch<Vehicle>(`${API_ENDPOINTS.VEHICLES}company/`, {
    method: 'POST',
    body: JSON.stringify({ registration_number, fuel_type, make, model, year }),
  });
};

export const getCompanyDrivers = async (
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  ordering?: string
): Promise<ApiResponse<any>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (search) params.append('search', search);
  if (ordering) params.append('ordering', ordering);

  return apiFetch<any>(`${API_ENDPOINTS.COMPANY_DRIVERS}?${params.toString()}`, {
    method: 'GET',
  });
};

export const updateCompanyVehicle = async (
  vehicleId: number,
  data: Partial<Pick<Vehicle, 'registration_number' | 'fuel_type' | 'make' | 'model' | 'year'>>
): Promise<ApiResponse<Vehicle>> => {
  return apiFetch<Vehicle>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/company/update/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

export const removeCompanyVehicle = async (vehicleId: number): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/company/`, {
    method: 'DELETE',
  });
};

export const assignDriverToVehicle = async (
  vehicleId: number,
  payload: { employment_id?: number; driver_id?: number }
): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/assign/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const unassignDriverFromVehicle = async (
  vehicleId: number,
  payload: { employment_id?: number; driver_id?: number }
): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/unassign/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

// ============ Driver self-management vehicle endpoints ============

/**
 * Get all vehicles assigned to the authenticated driver
 */
export const getMyVehicles = async (): Promise<ApiResponse<{ has_vehicles: boolean; vehicles: Vehicle[] }>> => {
  return apiFetch<{ has_vehicles: boolean; vehicles: Vehicle[] }>(`${API_ENDPOINTS.VEHICLES}my-vehicles/`, {
    method: 'GET',
  });
};

/**
 * Add a new vehicle to the driver's fleet (or assign existing vehicle)
 */
export const addMyVehicle = async (
  registration_number: string,
  fuel_type: string,
  make: string,
  model: string,
  year: number
): Promise<ApiResponse<Vehicle>> => {
  return apiFetch<Vehicle>(`${API_ENDPOINTS.VEHICLES}my-vehicles/`, {
    method: 'POST',
    body: JSON.stringify({ registration_number, fuel_type, make, model, year }),
  });
};

/**
 * Update a vehicle's details (registration_number is immutable)
 * Only make, model, year, and fuel_type can be changed
 */
export const updateMyVehicle = async (
  vehicleId: number,
  data: Partial<Pick<Vehicle, 'fuel_type' | 'make' | 'model' | 'year'>>
): Promise<ApiResponse<Vehicle>> => {
  return apiFetch<Vehicle>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/my-vehicles/update/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Remove a vehicle from driver's fleet (logical delete - preserves vehicle record)
 */
export const unassignMyVehicle = async (vehicleId: number): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.VEHICLES}${vehicleId}/my-vehicles/unassign/`, {
    method: 'POST',
  });
};

export const startSession = async (
  vehicle: number,
  odometer: number,
  location: string,
  confirmVelocity?: boolean
): Promise<ApiResponse<SessionActionResponse>> => {
  return apiFetch<SessionActionResponse>(`${API_ENDPOINTS.TIMELOGS}start-session/`, {
    method: 'POST',
    body: JSON.stringify({ vehicle, odometer, location, confirm_velocity: confirmVelocity }),
  });
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Driving actions carry ~400 byte payloads, so they get a tighter deadline than
// the default, which has to accommodate the unpaginated /api/timelogs/ list.
const DRIVING_ACTION_TIMEOUT_MS = 15000;

// Retries fire in milliseconds when the transport fails outright, so the retry
// count alone is a poor bound once timeouts are in play: 3 retries at 15s each
// would keep a driver staring at a spinner for over a minute. Cap the wall
// clock instead, which leaves fast failures with their full retry budget.
const RETRY_TOTAL_BUDGET_MS = 40000;

async function withNetworkRetry<T>(
  fn: () => Promise<ApiResponse<T>>,
  maxRetries = 3,
  baseDelayMs = 1000,
  maxTotalMs = RETRY_TOTAL_BUDGET_MS
): Promise<ApiResponse<T>> {
  const startedAt = Date.now();
  let response = await fn();

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (response.ok || response.errorType !== 'NETWORK') {
      return response;
    }

    if (Date.now() - startedAt >= maxTotalMs) {
      return response;
    }

    await delay(baseDelayMs * Math.pow(2, attempt));
    response = await fn();
  }

  return response;
}

// Kept alongside stopDriving deliberately: the two are the same operation
// mirrored, and start-driving was the one left without transport hardening,
// which is how a single dropped request stranded a driver's whole session (#316).
export const startDriving = async (
  vehicle: number,
  location: string
): Promise<ApiResponse<SessionActionResponse>> => {
  return withNetworkRetry(() =>
    apiFetch<SessionActionResponse>(`${API_ENDPOINTS.TIMELOGS}start-driving/`, {
      method: 'POST',
      body: JSON.stringify({ vehicle, location }),
      timeoutMs: DRIVING_ACTION_TIMEOUT_MS,
    })
  );
};

export const stopDriving = async (
  vehicle: number,
  location: string
): Promise<ApiResponse<SessionActionResponse>> => {
  return withNetworkRetry(() =>
    apiFetch<SessionActionResponse>(`${API_ENDPOINTS.TIMELOGS}stop-driving/`, {
      method: 'POST',
      body: JSON.stringify({ vehicle, location }),
      timeoutMs: DRIVING_ACTION_TIMEOUT_MS,
    })
  );
};

export const getTimeLogs = async (): Promise<ApiResponse<TimeLogEntry[]>> => {
  return apiFetch<TimeLogEntry[]>(API_ENDPOINTS.TIMELOGS, { method: 'GET' });
};

export const updateTimeLogEntry = async (
  id: number,
  data: Partial<Pick<TimeLogEntry, 'odometer' | 'location'>> & { edited?: boolean; observations?: string | null }
): Promise<ApiResponse<TimeLogEntry | ApiResponseError>> => {
  return apiFetch<TimeLogEntry | ApiResponseError>(`${API_ENDPOINTS.TIMELOGS}${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

export const correctOdometer = async (
  entryId: number,
  odometer: number,
  observations: string
): Promise<ApiResponse<TimeLogEntry | ApiResponseError>> => {
  return apiFetch<TimeLogEntry | ApiResponseError>(`${API_ENDPOINTS.TIMELOGS}${entryId}/correct-odometer/`, {
    method: 'PATCH',
    body: JSON.stringify({ odometer, observations }),
  });
};

export const getDailyDrivingTime = async (): Promise<ApiResponse<{ driving_time_today: string }>> => {
  return apiFetch<{ driving_time_today: string }>(`${API_ENDPOINTS.TIMELOGS}daily-driving-time/`, {
    method: 'GET',
  });
};

export const getCurrentDriverState = async (): Promise<ApiResponse<{ is_session_active: boolean; is_driving: boolean }>> => {
  return apiFetch<{ is_session_active: boolean; is_driving: boolean }>(`${API_ENDPOINTS.TIMELOGS}current-driver-state/`, {
    method: 'GET',
  });
};

export const getDriverStatus = async (): Promise<ApiResponse<DriverStatusResponse>> => {
  return apiFetch<DriverStatusResponse>(`${API_ENDPOINTS.TIMELOGS}driver-status/`, {
    method: 'GET',
  });
};

export const getWeeklyDrivingReport = async (weekStart?: string): Promise<ApiResponse<WeeklyDrivingReport>> => {
  const url = weekStart
    ? `${API_ENDPOINTS.REPORTS}weekly-driving-report/?week_start=${weekStart}`
    : `${API_ENDPOINTS.REPORTS}weekly-driving-report/`;
  return apiFetch<WeeklyDrivingReport>(url, { method: 'GET' });
};

export const getWeeklyDrivingReportPDF = async (weekStart?: string): Promise<ApiResponse<Blob>> => {
  const url = weekStart
    ? `${API_ENDPOINTS.REPORTS}weekly-driving-report/pdf?week_start=${weekStart}`
    : `${API_ENDPOINTS.REPORTS}weekly-driving-report/pdf`;
  return apiFetchBlob(url, { method: 'GET' });
};

export const getOwnerDriverWeeklyDrivingReportPDF = async (
  employmentId: string | number,
  weekStart?: string,
  includeHidden = false,
): Promise<ApiResponse<Blob>> => {
  return apiFetchBlob(API_ENDPOINTS.OWNER_DRIVER_WEEKLY_REPORT_PDF(employmentId, weekStart, includeHidden), { method: 'GET' });
};

export const getDailyDrivingReportPDF = async (date?: string): Promise<ApiResponse<Blob>> => {
  const url = date
    ? `${API_ENDPOINTS.REPORTS}daily-driving-report/pdf?date=${date}`
    : `${API_ENDPOINTS.REPORTS}daily-driving-report/pdf`;
  return apiFetchBlob(url, { method: 'GET' });
};

export const updateNotificationPreferences = async (
  enabled: boolean,
  stopTimeThreshold: number,
  distanceThreshold: number
): Promise<ApiResponse<any>> => {
  return apiFetch<any>(API_ENDPOINTS.USER_NOTIFICATION_PREFERENCES, {
    method: 'PUT',
    body: JSON.stringify({ 
      notification_enabled: enabled, 
      stop_time_threshold: stopTimeThreshold,
      distance_threshold: distanceThreshold 
    }),
  });
};

/**
 * Explicit Hours payer for a declared company relationship (D-053). The backend
 * serializer requires it; an association without a payer is exactly what made
 * the resume/employment path fail with a required-field 400 and save nothing.
 * Modelled as a required field so every caller must state the driver's choice —
 * never inferred from persona, subscriptions or capacity.
 */
export type AssociateDriverPayer = 'self' | 'company';

export interface AssociateDriverPayload {
  reconsider_initial_payer?: boolean;
  company_name: string;
  company_nipc: string;
  company_email?: string;
  company_phone?: string;
  payer: AssociateDriverPayer;
  work_identity_kind?: 'individual' | 'collective';
  confirm_company_change?: boolean;
  expected_relationship_id?: number;
}

export interface DriverWorkContext {
  work_company: { id: number; name: string; tax_identifier_kind: 'nif' | 'nipc'; tax_identifier: string; identity_source: string | null } | null;
  relationship_id: number | null;
  relationship_status: string;
  license: { payer: string | null; interval: 'month' | 'year' | null; coverage_status: string };
  pending_reason: string | null;
  can_start_new_session: boolean;
  open_session_context: { company_id: number | null; company_name: string; scope_id: number | null; vehicle_id: number | null } | null;
  future_funding: { payer: 'company'; effective_at: string } | null;
}

export const getDriverWorkContext = async (): Promise<ApiResponse<DriverWorkContext>> =>
  apiFetch<DriverWorkContext>(`${API_BASE}/api/companies/driver/work-context/`, { method: 'GET' }, true, false);

export const associateDriver = async (
  data: AssociateDriverPayload
): Promise<ApiResponse<any>> => {
  return apiFetch<any>(API_ENDPOINTS.ADD_COMPANY, {
    method: 'POST',
    // Bounded, buildInfo-derived client metadata for the backend association
    // validation audit boundary (companies.association_validation). No user
    // input/PII and no minimum-version enforcement: the backend records
    // sanitized platform/release only.
    headers: {
      'X-Client-Platform': CLIENT_PLATFORM,
      'X-Client-Release': CLIENT_RELEASE,
    },
    body: JSON.stringify(data),
  });
};

// Common NIPC lookup (MR !388 final decision 2): the backend does the lookup
// BEFORE the paywall decision. Returns only public identity (name + tax id) when
// the NIPC is known; {found:false} otherwise. Fail-closed: never leaks owner/
// license/invitation state (see backend CompanyViewSet.lookup_company).
export const lookupCompany = async (
  nipc: string
): Promise<ApiResponse<{ found: boolean; name?: string; tax_identifier?: string }>> => {
  // PRIVATE LOOKUP (auth required): registration creates/authenticates the
  // driver once after personal data and BEFORE the NIPC step, then persists
  // the token. Anonymous callers must get 401/403 from the backend.
  return apiFetch<{ found: boolean; name?: string; tax_identifier?: string }>(
    `${API_ENDPOINTS.COMPANY_LOOKUP}?nipc=${encodeURIComponent(nipc)}`,
    { method: 'GET' },
    true,
    false
  );
};

// The owner driver-profile boundary stores identity/profile data only. Driver
// activation remains an explicit onboarding outcome (invitation or self-pay).
export interface DriverActivationState {
  profile_completed: boolean;
  driver_activated: boolean;
  employment_created: boolean;
  license_required: boolean;
}

export interface DriverProfileBoundaryResponse {
  detail: string;
  profile_completed: boolean;
  driver_activated: boolean;
  employment_created: boolean;
  /** D-046: profile saved but no company seat was available to activate. */
  license_required: boolean;
  /** D-009/D-010/D-053: a live personal paid period keeps its current payer. */
  personal_payer_active?: boolean;
  /** D-013: a different current company relationship blocks activation here. */
  relationship_conflict?: boolean;
  outcome: string;
  driver_state?: DriverActivationState;
}

export const saveOwnerDriverProfile = async (
  data: {
    full_name: string;
    phone_number: string;
    nif: string;
    address: string;
    license_number: string;
    date_of_birth: string;
  }
): Promise<ApiResponse<DriverProfileBoundaryResponse>> => {
  const response = await apiFetch<DriverProfileBoundaryResponse>(API_ENDPOINTS.COMPLETE_DRIVER_PROFILE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  // A successful write must invalidate the cached users/me GET snapshot, or a
  // follow-up read within the short cache window returns the pre-write state
  // (stale profile / stale driver_activation) — the D-046 stale-UI defect.
  if (response.ok) clearApiCache(API_ENDPOINTS.USER_DETAILS);
  return response;
};

/** @deprecated Use saveOwnerDriverProfile; this endpoint no longer activates a driver. */
export const completeDriverProfile = saveOwnerDriverProfile;

/**
 * D-046 explicit activation/retry using the already-saved driver profile.
 * For an owner whose profile was saved but not activated (no licence at save
 * time, or saved under the old profile-only behaviour). Idempotent; never
 * charges, starts a trial or changes billing.
 */
export const activateOwnerDriverProfile = async (): Promise<ApiResponse<DriverProfileBoundaryResponse>> => {
  const response = await apiFetch<DriverProfileBoundaryResponse>(API_ENDPOINTS.ACTIVATE_DRIVER_PROFILE, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  // Same write→read invalidation as saveOwnerDriverProfile: the retry path
  // must not read a cached pre-activation users/me snapshot.
  if (response.ok) clearApiCache(API_ENDPOINTS.USER_DETAILS);
  return response;
};

// New function to update general user profile details.
// NOTE: `email` is intentionally NOT part of this payload — the backend
// rejects attempts to change it here (#188). Use requestEmailChange()
// instead, which starts the two-step confirmation flow.
export const updateUserDetails = async (userData: Partial<UserDetails['profile'] & { full_name?: string; phone_number?: string | null }>): Promise<ApiResponse<UserDetails>> => {
  return apiFetch<UserDetails>(`${API_ENDPOINTS.USER_DETAILS}`, {
    method: 'PATCH',
    body: JSON.stringify(userData),
  });
};

// Interface for what a single user vehicle might look like, can be same as Vehicle for now
export interface UserVehicle extends Vehicle {}

// Function to get the current user's primary vehicle
export const getMyVehicle = async (): Promise<ApiResponse<UserVehicle | null>> => {
  const response = await getDriverVehicles();
  if (response.ok) {
    if (response.data && response.data.vehicles && response.data.vehicles.length > 0) {
      // Assuming the first vehicle is the one to be considered "my vehicle"
      return { ok: true, status: response.status, data: response.data.vehicles[0] as UserVehicle };
    } else {
      // No vehicles found for the user, or data structure is not as expected
      return { ok: true, status: response.status, data: null };
    }
  }
  // If the response was not ok, propagate the original error response
  // The data part of response might not match UserVehicle | null, but it's an error state.
  return { ok: false, status: response.status, data: response.data as any }; 
};

// Company Transfer Request
export interface CompanyTransferRequestPayload {
  new_company_name: string;
  new_company_nipc: string;
  reason: string;
  company_email?: string;  // Optional
  company_phone?: string;  // Optional
}

export interface CompanyTransferRequestResponse { 
    id: number;
    user: number;
    user_name: string;
    user_email: string;
    current_company: number;
    current_company_name: string;
    new_company?: number;
    new_company_name: string;
    new_company_name_display?: string;
    new_company_nipc: string;
    company_email?: string;  // Optional
    company_phone?: string;  // Optional
    reason: string;
    status: string; 
    created_at: string;
    updated_at: string;
}

export const submitCompanyTransferRequest = async (payload: CompanyTransferRequestPayload): Promise<ApiResponse<CompanyTransferRequestResponse>> => {
  const endpoint = (API_ENDPOINTS as { REQUEST_COMPANY_TRANSFER?: string }).REQUEST_COMPANY_TRANSFER;
  if (!endpoint) {
    return Promise.resolve({
        ok: false,
        status: 500,
        data: { message: 'API endpoint for company transfer request is not configured.' } as any,
    });
  }
  return apiFetch<CompanyTransferRequestResponse>(endpoint, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

// Get pending incoming transfer requests for company owners
export const getPendingIncomingTransfers = async (): Promise<ApiResponse<CompanyTransferRequestResponse[]>> => {
  return apiFetch<CompanyTransferRequestResponse[]>(API_ENDPOINTS.PENDING_INCOMING_TRANSFERS, {
    method: 'GET',
  });
};

// Get outgoing transfer requests for current user
export const getOutgoingTransfers = async (): Promise<ApiResponse<CompanyTransferRequestResponse[]>> => {
  return apiFetch<CompanyTransferRequestResponse[]>(API_ENDPOINTS.OUTGOING_TRANSFERS, {
    method: 'GET',
  });
};

// Approve a transfer request
export const approveTransferRequest = async (transferId: number): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.REQUEST_COMPANY_TRANSFER}${transferId}/approve/`, {
    method: 'POST',
  });
};

// Reject a transfer request
export const rejectTransferRequest = async (transferId: number): Promise<ApiResponse<{ detail: string }>> => {
  return apiFetch<{ detail: string }>(`${API_ENDPOINTS.REQUEST_COMPANY_TRANSFER}${transferId}/reject/`, {
    method: 'POST',
  });
};

// Add this interface for language update
interface LanguageUpdatePayload {
  language: string;
}

// Add this function to update language
export const updateUserLanguage = async (language: string): Promise<ApiResponse<UserDetails>> => {
  try {
    const payload: LanguageUpdatePayload = { language };
    const response = await apiFetch<UserDetails>(API_ENDPOINTS.USER_LANGUAGE, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      await setItem(LANGUAGE_KEY, language);
    }
    return response;
  } catch (error: any) {
    return {
      ok: false,
      data: error.response?.data || { message: 'Failed to update language' },
      status: error.response?.status || 500,
    };
  }
};

// Function to get the user's language preference from the backend
export const getUserLanguage = async (): Promise<ApiResponse<{ language: string }>> => {
  try {
    return await apiFetch<{ language: string }>(API_ENDPOINTS.USER_LANGUAGE, { method: 'GET' });
  } catch (error: any) {
    return { ok: false, data: { language: '' }, status: 500 };
  }
};

// Add interface for driver registration payload
interface RegisterDriverPayload {
  email: string;
  password: string;
  full_name: string;
  phone_number: string;
  profile: {
    nif: string;
    address: string;
    license_number: string;
    date_of_birth: string;
  };
  // D-057: optional referral fields carried by the signup request itself so the
  // backend freezes attribution atomically with account creation.
  referral_code?: string;
  referral_token?: string;
}

// Add interface for driver registration response
interface RegisterDriverResponse {
  key?: string;       // login key for non-invite flow
  token?: string;     // auth token for invited users
  user?: {
    email: string;
    full_name: string;
  };
  detail?: string;    // optional message
}

// Add registerDriver function
export const registerDriver = async (
  data: RegisterDriverPayload,
  invitationToken?: string
): Promise<ApiResponse<RegisterDriverResponse>> => {
  const endpoint = invitationToken
    ? `${API_ENDPOINTS.REGISTER_DRIVER}?invitation_token=${invitationToken}`
    : API_ENDPOINTS.REGISTER_DRIVER;
  return apiFetch<RegisterDriverResponse>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  }, false);
};

export const logout = async (): Promise<ApiResponse<null>> => {
    try {
        // Revoke while the credential still exists; local cleanup is not
        // evidence that the server stopped accepting the bearer.
        const token = await getItem('userToken');
        const revocation = token
            ? await apiFetch<null>(API_ENDPOINTS.LOGOUT, {
                method: 'POST', includeCompanyContext: false, timeoutMs: 5000,
              }, true, false)
            : null;
        await Promise.all([
            removeItem('userToken'),
            clearOwnerDriverStorage(),
        ]);
        activeCompanyIdCache = null;
        await removeItem(SELECTED_COMPANY_ID_STORAGE_KEY);
        clearApiCache();
        if (revocation && !revocation.ok) return { ...revocation, data: null };
        return {
            ok: true,
            data: null,
            status: 200
        };
    } catch (error) {
        console.error('Logout error:', error);
        return {
            ok: false,
            data: null,
            status: 500
        };
    }
};

export const getCompanyLicenses = async (companyId?: number | string): Promise<ApiResponse<CompanyLicenses>> => {
  // Bypass cache to ensure fresh data after cancel/reactivate actions
  return apiFetch<CompanyLicenses>(
    `${API_ENDPOINTS.COMPANIES}me/licenses/`,
    withCompanyContext({ method: 'GET' }, companyId),
    true,
    false
  );
};

export const getOwnedCompanies = async (): Promise<ApiResponse<OwnedCompanySummary[]>> => {
  return apiFetch<OwnedCompanySummary[]>(
    `${API_ENDPOINTS.COMPANIES}mine/`,
    { method: 'GET' }
  );
};

// Transfer vehicle between companies owned by the same owner
export interface TransferVehiclePayload {
  vehicle_id: number;
  target_company_id: number;
  transfer_related_drivers?: boolean;
}

export interface TransferVehicleResponse {
  detail: string;
  vehicle_id: number;
  source_company: string;
  target_company: string;
}

export const transferVehicle = async (
  payload: TransferVehiclePayload
): Promise<ApiResponse<TransferVehicleResponse>> => {
  return apiFetch<TransferVehicleResponse>(
    `${API_ENDPOINTS.COMPANIES}transfer-vehicle/`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

// Transfer driver between companies owned by the same owner
export interface TransferDriverPayload {
  employment_id: number;
  target_company_id: number;
  transfer_related_vehicles?: boolean;
}

export interface TransferDriverResponse {
  detail: string;
  employment_id: number;
  source_company: string;
  target_company: string;
}

export const transferDriver = async (
  payload: TransferDriverPayload
): Promise<ApiResponse<TransferDriverResponse>> => {
  return apiFetch<TransferDriverResponse>(
    `${API_ENDPOINTS.COMPANIES}transfer-driver/`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

// Cancel a subscription at period end
export const cancelSubscription = async (
  subscriptionId: string,
  companyId?: number | string
): Promise<ApiResponse<{ status: string; message: string; cancel_at_period_end: boolean }>> => {
  return apiFetch<{ status: string; message: string; cancel_at_period_end: boolean }>(
    API_ENDPOINTS.CANCEL_SUBSCRIPTION,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify({ subscription_id: subscriptionId, action: 'cancel' }),
    }, companyId)
  );
};

// Reactivate a canceled subscription
export const reactivateSubscription = async (
  subscriptionId: string,
  companyId?: number | string
): Promise<ApiResponse<{ status: string; message: string; cancel_at_period_end: boolean }>> => {
  return apiFetch<{ status: string; message: string; cancel_at_period_end: boolean }>(
    API_ENDPOINTS.CANCEL_SUBSCRIPTION,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify({ subscription_id: subscriptionId, action: 'reactivate' }),
    }, companyId)
  );
};

// Interface for company owner dashboard statistics
export interface CompanyStatistics {
  currently_driving: number;
  avg_drive_time_hours: number;
  avg_distance_km: number;
}

// Interface for active users statistics
export interface ActiveUsersStatistics {
  dau: number[]; // Daily Active Users for last 12 days
  mau: number[]; // Monthly Active Users for last 12 months
}

// Interface for vehicle statistics
export interface VehicleStatistics {
  oldest_vehicles: Array<{
    name: string;
    year: number;
    registration_number: string;
  }>;
  high_odometer: Array<{
    name: string;
    km: number;
    registration_number: string;
  }>;
}

// Interface for invitation statistics
export interface InvitationStatistics {
  last7: {
    sent: number;
    accepted: number;
    expired: number;
  };
  last30: {
    sent: number;
    accepted: number;
    expired: number;
  };
}

// Fetch company statistics for owner dashboard
export const getCompanyStatistics = async (
  companyId?: number | string
): Promise<ApiResponse<CompanyStatistics>> => {
  return apiFetch<CompanyStatistics>(
    `${API_ENDPOINTS.COMPANIES}me/statistics/`,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Fetch active users statistics (DAU/MAU) for owner dashboard
export const getActiveUsersStatistics = async (
  companyId?: number | string
): Promise<ApiResponse<ActiveUsersStatistics>> => {
  const url = `${API_ENDPOINTS.COMPANIES}me/active-users/`;
  return apiFetch<ActiveUsersStatistics>(
    url,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Fetch vehicle statistics for owner dashboard
export const getVehicleStatistics = async (
  companyId?: number | string
): Promise<ApiResponse<VehicleStatistics>> => {
  const url = `${API_ENDPOINTS.COMPANIES}me/vehicle-statistics/`;
  return apiFetch<VehicleStatistics>(
    url,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Fetch invitation statistics for owner dashboard
export const getInvitationStatistics = async (
  companyId?: number | string
): Promise<ApiResponse<InvitationStatistics>> => {
  const url = `${API_ENDPOINTS.COMPANIES}me/invitation-statistics/`;
  return apiFetch<InvitationStatistics>(
    url,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Interface for last known driver location
export interface DriverLastLocation {
  id: number;
  name: string;
  location: { latitude: number; longitude: number };  // Coordinates
  isDriving: boolean;
  lastUpdate: string;
}

// Fetch last known location for each active driver
export const getCompanyDriverLocations = async (
  companyId?: number | string
): Promise<ApiResponse<DriverLastLocation[]>> => {
  return apiFetch<DriverLastLocation[]>(
    `${API_ENDPOINTS.COMPANIES}me/driver-locations/`,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Fetch current owner's company details
export const getMyCompany = async (companyId?: number | string): Promise<ApiResponse<Company>> => {
  return apiFetch<Company>(
    `${API_ENDPOINTS.COMPANIES}me/`,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Update current owner's company details
export const updateMyCompany = async (
  data: Partial<Pick<Company, 'name' | 'address' | 'phone_number' | 'email' | 'certidao_permanente'>>,
  companyId?: number | string
): Promise<ApiResponse<Company>> => {
  return apiFetch<Company>(
    `${API_ENDPOINTS.COMPANIES}me/`,
    withCompanyContext(
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      companyId
    )
  );
};

export interface CreateSubscriptionPayload {
  price_id: string;
  quantity: number;
  setup_price_id?: string;
  device_quantity?: number;
}

export interface CreateSubscriptionResponse {
  subscription_id: string;
  client_secret?: string;
  status: string;
}

export const createSubscription = async (
  data: CreateSubscriptionPayload,
  companyId?: number | string
): Promise<ApiResponse<CreateSubscriptionResponse>> => {
  const fingerprint = stripeOperationFingerprint('create-subscription', data, companyId);
  return apiFetchWithOperationToken<CreateSubscriptionResponse>(
    fingerprint,
    `${API_ENDPOINTS.SUBSCRIPTIONS}create_subscription/`,
    withCompanyContext(
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      companyId
    )
  );
};

export interface UpdatePaymentMethodResponse {
  client_secret: string;
}

export const updatePaymentMethod = async (
  subscriptionId: string,
  companyId?: number | string
): Promise<ApiResponse<UpdatePaymentMethodResponse>> => {
  const payload = { subscription_id: subscriptionId };
  const fingerprint = stripeOperationFingerprint('update-payment-method', payload, companyId);
  return apiFetchWithOperationToken<UpdatePaymentMethodResponse>(
    fingerprint,
    API_ENDPOINTS.UPDATE_PAYMENT_METHOD,
    withCompanyContext(
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      companyId
    )
  );
};

export const cancelUpdatePaymentMethodOperation = (
  subscriptionId: string,
  companyId?: number | string
): void => {
  cancelStripeOperation(stripeOperationFingerprint(
    'update-payment-method',
    { subscription_id: subscriptionId },
    companyId
  ));
};

export interface SyncSubscriptionResponse {
  subscription_id: string;
  status: string;
  message?: string;
}

export const syncSubscription = async (
  subscriptionId: string,
  companyId?: number | string
): Promise<ApiResponse<SyncSubscriptionResponse>> => {
  return apiFetch<SyncSubscriptionResponse>(
    `${API_ENDPOINTS.SUBSCRIPTIONS}sync_subscription/`,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify({ 
        subscription_id: subscriptionId
      }),
    }, companyId)
  );
};

// Confirm payment success and activate subscription immediately
export const confirmPayment = async (
  subscriptionId: string,
  paymentIntentId?: string,
  companyId?: number | string
): Promise<ApiResponse<{ subscription_id: string; status: string; licenses: number; message: string }>> => {
  return apiFetch<{ subscription_id: string; status: string; licenses: number; message: string }>(
    `${API_ENDPOINTS.SUBSCRIPTIONS}confirm_payment/`,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify({
        subscription_id: subscriptionId,
        payment_intent_id: paymentIntentId,
      }),
    }, companyId)
  );
};

// Create a Stripe billing portal session
export const createBillingPortalSession = async (
  frontendReturnUrl: string,
  stripeApiReturnUrl: string,
  isMobile: boolean, // Added isMobile parameter
  companyId?: number | string
): Promise<ApiResponse<{ url: string }>> => {
  return apiFetch<{ url: string }>(
    API_ENDPOINTS.BILLING_PORTAL,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify({
        return_url: stripeApiReturnUrl,
        frontend_return_url: frontendReturnUrl,
        is_mobile: isMobile, // Pass isMobile in the request body
      }),
    }, companyId)
  );
};

export const createSelfPayBillingPortalSession = async (
  frontendReturnUrl: string,
  stripeApiReturnUrl: string,
  isMobile: boolean,
): Promise<ApiResponse<{ url: string }>> => {
  return apiFetch<{ url: string }>(API_ENDPOINTS.BILLING_PORTAL, {
    method: 'POST',
    includeCompanyContext: false,
    body: JSON.stringify({
      billing_context: 'driver_self_pay',
      return_url: stripeApiReturnUrl,
      frontend_return_url: frontendReturnUrl,
      is_mobile: isMobile,
    }),
  });
};

// Interface for driver invitation data fetched from backend
export interface DriverInvitationData {
  email: string;
  token: string;
  full_name?: string | null;
  phone_number?: string | null;
  address?: string | null;
  license_number?: string | null;
  date_of_birth?: string | null;
  company_name: string;
  company_nipc: string;
  existing_user: boolean;
  has_employment: boolean;
  is_active_employment: boolean;
  employment_id?: number | null;
  expires_at?: string;
  is_expired?: boolean;
  expiration_days?: number;
  license_payer?: 'company' | 'self';
  created_at?: string;
  renewal_requested_at?: string | null;
}

// #207 matrix C5: the shape accept_invitation's GET returns for an expired
// invitation — a structured code + enough context for an explanatory screen
// instead of a generic error string.
export interface ExpiredInvitationDetail {
  detail: string;
  code: 'invitation_expired';
  company_name: string;
  email: string;
  renewal_requested: boolean;
}

// Fetch invitation details for pre-fill (no auth required)
export const getDriverInvitation = async (
  token: string
): Promise<ApiResponse<DriverInvitationData | ExpiredInvitationDetail>> => {
  return apiFetch<DriverInvitationData | ExpiredInvitationDetail>(
    `${API_ENDPOINTS.DRIVER_INVITATION}${token}/`,
    { method: 'GET' },
    false,
    false
  );
};

// #207 matrix C2: look up a pending invitation by the driver's own email
// (public — called during registration, before the driver has a session).
export interface InvitationByEmailResponse {
  has_pending: boolean;
  requires_invitation_link?: boolean;
  invitation?: DriverInvitationData;
}

export const getInvitationByEmail = async (
  email: string
): Promise<ApiResponse<InvitationByEmailResponse>> => {
  return apiFetch<InvitationByEmailResponse>(
    `${API_ENDPOINTS.INVITATION_BY_EMAIL}?email=${encodeURIComponent(email)}`,
    { method: 'GET' },
    false,
    false
  );
};

// #207 matrix C5: ask the owner for a fresh invite after this one expired
// (public — the invitee may not have an account/session yet).
export const requestInvitationRenewal = async (
  token: string
): Promise<ApiResponse<{ detail?: string }>> => {
  return apiFetch<{ detail?: string }>(
    API_ENDPOINTS.INVITATION_REQUEST_RENEWAL(token),
    { method: 'POST' },
    false,
    false
  );
};

// Accept the invitation and create active employment (requires auth)
export const acceptDriverInvitation = async (
  token: string,
  context?: { confirm_company_change: true; expected_relationship_id: number },
): Promise<ApiResponse<Employment>> => {
  const endpoint = `${API_ENDPOINTS.DRIVER_INVITATION}${token}/`;
  return apiFetch<Employment>(
    endpoint,
    { method: 'POST', ...(context ? { body: JSON.stringify(context) } : {}) }
  );
};

// Fetch all pending driver invitations for the company owner
export const getCompanyInvitations = async (
  companyId?: number | string
): Promise<ApiResponse<DriverInvitationData[]>> => {
  return apiFetch<DriverInvitationData[]>(
    `${API_ENDPOINTS.COMPANIES}me/invitations/`,
    withCompanyContext({ method: 'GET' }, companyId)
  );
};

// Resend a pending driver invitation
export const resendDriverInvitation = async (
  token: string,
  companyId?: number | string
): Promise<ApiResponse<{ detail: string }>> => {
  try {
    return await apiFetch<{ detail: string }>(
      `${API_ENDPOINTS.COMPANIES}me/invitations/${token}/resend/`,
      withCompanyContext({ method: 'POST' }, companyId)
    );
  } catch (error: any) {
    return {
      ok: false,
      data: { detail: error.message || 'Error resending invitation' },
      status: error.response?.status || 500,
    };
  }
};

export const cancelDriverInvitation = async (
  token: string,
  companyId?: number | string
): Promise<ApiResponse<{ detail: string }>> => {
  try {
    return await apiFetch<{ detail: string }>(
      `${API_ENDPOINTS.COMPANIES}me/invitations/${token}/cancel/`,
      withCompanyContext({ method: 'POST' }, companyId)
    );
  } catch (error: any) {
    return {
      ok: false,
      data: { detail: error.message || 'Error cancelling invitation' } as any,
      status: error.response?.status || 500,
    };
  }
};

// Bulk activate drivers
export interface BulkActivateDriversPayload {
  employment_ids?: number[]; // Optional: if not provided, activates all pending
}

export interface BulkActivateDriversResponse {
  success_count: number;
  error_count: number;
}

export const bulkActivateDrivers = async (
  payload?: BulkActivateDriversPayload,
  companyId?: number | string
): Promise<ApiResponse<BulkActivateDriversResponse>> => {
  return apiFetch<BulkActivateDriversResponse>(
    API_ENDPOINTS.BULK_ACTIVATE_DRIVERS,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }, companyId)
  );
};

// Bulk transfer drivers
export interface BulkTransferDriversPayload {
  employment_ids: number[];
  target_company_id: number;
  transfer_related_vehicles?: boolean;
}

export interface BulkTransferDriversResponse {
  success_count: number;
  error_count: number;
  errors?: Array<{
    employment_id: number;
    error: string;
  }>;
}

export const bulkTransferDrivers = async (
  payload: BulkTransferDriversPayload,
  companyId?: number | string
): Promise<ApiResponse<BulkTransferDriversResponse>> => {
  return apiFetch<BulkTransferDriversResponse>(
    API_ENDPOINTS.BULK_TRANSFER_DRIVERS,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify(payload),
    }, companyId)
  );
};

// Update driver information
export interface UpdateDriverPayload {
  full_name?: string;
  email?: string;
  phone_number?: string;
  address?: string;
  license_number?: string;
  is_active?: boolean;
  report_hidden?: boolean;
}

export const updateDriver = async (
  employmentId: number,
  data: UpdateDriverPayload,
  companyId?: number | string
): Promise<ApiResponse<Employment>> => {
  return apiFetch<Employment>(
    `${API_ENDPOINTS.UPDATE_DRIVER}${employmentId}/`,
    withCompanyContext({
      method: 'PATCH',
      body: JSON.stringify(data),
    }, companyId)
  );
};

export interface TrialSetupIntentPayload {
  price_id: string;
  quantity: number;
}

export interface TrialSetupIntentResponse {
  setup_intent_id: string;
  setup_intent_client_secret: string;
}

export const createTrialSetupIntent = async (
  data: TrialSetupIntentPayload,
  companyId?: number | string
): Promise<ApiResponse<TrialSetupIntentResponse>> => {
  const fingerprint = stripeOperationFingerprint('trial-setup-intent', data, companyId);
  return apiFetchWithOperationToken<TrialSetupIntentResponse>(
    fingerprint,
    `${API_ENDPOINTS.SUBSCRIPTIONS}trial_setup_intent/`,
    withCompanyContext({
      method: 'POST',
      body: JSON.stringify(data),
    }, companyId)
  );
};

export const cancelTrialSetupIntentOperation = (
  data: TrialSetupIntentPayload,
  companyId?: number | string
): void => {
  cancelStripeOperation(stripeOperationFingerprint('trial-setup-intent', data, companyId));
};

export interface CreateTrialSubscriptionPayload {
  setup_intent_id: string;
  price_id: string;
  quantity: number;
}

export interface CreateTrialSubscriptionResponse {
  subscription_id: string;
  client_secret: string | null;
  status: string;
}

export const createTrialSubscription = async (
  data: CreateTrialSubscriptionPayload,
  companyId?: number | string
): Promise<ApiResponse<CreateTrialSubscriptionResponse>> => {
  const fingerprint = stripeOperationFingerprint('create-trial-subscription', data, companyId);
  return apiFetchWithOperationToken<CreateTrialSubscriptionResponse>(
    fingerprint,
    `${API_ENDPOINTS.SUBSCRIPTIONS}create_trial_subscription/`,
    withCompanyContext(
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      companyId
    )
  );
};

export interface CreateTrialSubscriptionNoCardPayload {
  price_id: string;
  quantity: number;
}

export interface CreateTrialSubscriptionNoCardResponse {
  subscription_id: string;
  status: string;
}

/**
 * WS2 — start the owner trial with NO card up front. Licenses become active on
 * a real Stripe trial subscription; the card is collected only at D-0 (the
 * mandatory paywall, #209). Replaces the SetupIntent card-collection step in the
 * owner funnel.
 */
export const createTrialSubscriptionNoCard = async (
  data: CreateTrialSubscriptionNoCardPayload,
  companyId?: number | string
): Promise<ApiResponse<CreateTrialSubscriptionNoCardResponse>> => {
  const fingerprint = stripeOperationFingerprint('create-trial-subscription-no-card', data, companyId);
  return apiFetchWithOperationToken<CreateTrialSubscriptionNoCardResponse>(
    fingerprint,
    `${API_ENDPOINTS.SUBSCRIPTIONS}create_trial_subscription_no_card/`,
    withCompanyContext(
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      companyId
    )
  );
};

export interface CheckoutSessionPayload {
  price_id: string;
  quantity: number;
  setup_price_id?: string;
  device_quantity?: number;
  return_url: string; // This is the URL Stripe will redirect to (HTTPS only)
  frontend_return_url: string; // This is the actual URL the frontend wants (HTTP or deep link)
  is_mobile?: boolean; // Added for unified redirect endpoint
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export const createCheckoutSession = async (
  data: CheckoutSessionPayload,
  companyId?: number | string
): Promise<ApiResponse<CheckoutSessionResponse>> => {
  const fingerprint = stripeOperationFingerprint('checkout-session', data, companyId);
  return apiFetchWithOperationToken<CheckoutSessionResponse>(
    fingerprint,
    API_ENDPOINTS.CHECKOUT_SESSION,
    withCompanyContext(
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      companyId
    )
  );
};

export const cancelCheckoutSessionOperation = (
  data: CheckoutSessionPayload,
  companyId?: number | string
): void => {
  cancelStripeOperation(stripeOperationFingerprint('checkout-session', data, companyId));
};

export interface SelfPayCheckoutPayload {
  return_url: string;
  /** Product chosen on the self-pay surface (#289). Omitted means 'hours'. */
  feature?: 'hours' | 'gps' | 'combo';
  /**
   * Exact catalog price and cycle the driver was shown (#291). The server
   * re-validates both against the active catalog — feature and interval must
   * match — and rejects a mismatch rather than substituting a price. Omitting
   * them keeps the previous server-chosen monthly plan.
   */
  price_id?: string;
  billing?: 'monthly' | 'annual';
  /**
   * Native only (#290): the app deep link Stripe must hand control back to, so
   * the in-app browser closes by itself. Validated server-side against the
   * app's own scheme allowlist; ignored unless `is_mobile` is true.
   */
  frontend_return_url?: string;
  is_mobile?: boolean;
}

export const createSelfPayCheckoutSession = async (
  data: SelfPayCheckoutPayload
): Promise<ApiResponse<{ checkout_url?: string; code?: string }>> => {
  const fingerprint = stripeOperationFingerprint('self-pay-checkout-session', data);
  return apiFetchWithOperationToken<{ checkout_url?: string; code?: string }>(
    fingerprint,
    API_ENDPOINTS.SELF_PAY_CHECKOUT,
    { method: 'POST', body: JSON.stringify(data) }
  );
};

export const cancelSelfPayCheckoutOperation = (data: SelfPayCheckoutPayload): void => {
  cancelStripeOperation(stripeOperationFingerprint('self-pay-checkout-session', data));
};

/** Published GPS contract version filled with the purchase being made (D-059). */
export interface GpsContractDocument {
  version_id: string;
  version_date: string;
  content_sha256: string;
  text: string;
  summary: string;
  owner_email: string;
}

/** Get the published GPS contract (summary + full contract with Annex A) for this purchase. */
export const getGpsContractText = async (
  priceId: string,
  quantity: number,
  companyId?: number | string
): Promise<ApiResponse<GpsContractDocument>> => {
  const query = `price_id=${encodeURIComponent(priceId)}&quantity=${encodeURIComponent(String(quantity))}`;
  return apiFetch<GpsContractDocument>(
    `${API_ENDPOINTS.SUBSCRIPTIONS}gps-contract-text/?${query}`,
    withCompanyContext({ method: 'GET' }, companyId),
    true,
    false
  );
};

export interface AcceptGpsContractPayload {
  version_id: string;
  price_id: string;
  quantity: number;
}

export interface AcceptGpsContractResponse {
  message: string;
  gps_contract_accepted_at: string | null;
  reference?: string;
  version_id?: string;
  content_sha256?: string;
  recipient_email?: string;
  code?: string;
}

/** Accept the GPS contract version shown for this purchase. A 409 means the version changed: refetch and show it. */
export const acceptCompanyGpsContract = async (
  payload: AcceptGpsContractPayload,
  companyId?: number | string
): Promise<ApiResponse<AcceptGpsContractResponse>> => {
  return apiFetch<AcceptGpsContractResponse>(
    `${API_ENDPOINTS.COMPANIES}me/accept-gps-contract/`,
    withCompanyContext({ method: 'POST', body: JSON.stringify(payload) }, companyId)
  );
};

// Verify reCAPTCHA token (public)
export const verifyRecaptcha = async (
  token: string,
  action: string
): Promise<ApiResponse<{ ok: boolean; score?: number }>> => {
  return apiFetch<{ ok: boolean; score?: number }>(API_ENDPOINTS.VERIFY_RECAPTCHA, {
    method: 'POST',
    body: JSON.stringify({ token, action }),
  }, false);
};

/**
 * API Service Object
 * Provides a convenient interface for making HTTP requests
 */
const apiService = {
  get: <T = any>(url: string, options?: ApiRequestInit & { fresh?: boolean }): Promise<ApiResponse<T>> => {
    const { fresh, ...requestOptions } = options ?? {};
    if (fresh) clearApiCache(url);
    return apiFetch<T>(url, { ...requestOptions, method: 'GET' }, true, !fresh);
  },

  post: <T = any>(url: string, data?: any, options?: ApiRequestInit): Promise<ApiResponse<T>> => {
    return apiFetch<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  put: <T = any>(url: string, data?: any, options?: ApiRequestInit): Promise<ApiResponse<T>> => {
    return apiFetch<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  patch: <T = any>(url: string, data?: any, options?: ApiRequestInit): Promise<ApiResponse<T>> => {
    return apiFetch<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  delete: <T = any>(url: string, options?: ApiRequestInit): Promise<ApiResponse<T>> => {
    return apiFetch<T>(url, { ...options, method: 'DELETE' });
  },
};

export default apiService;